import { WfWorkplace } from 'src/api/wf/models/WfWorkplace';
import LinkButton from 'src/ui/wf/common/button/LinkButton';
import P_CollapsibleSection from '../common/collapsibleSection/P_CollapsibleSection';
import { useTranslation } from 'react-i18next';
import { FormRow } from '../../common/form/FormRow';
import { FC } from 'react';
import { useUserAgent } from 'src/common/userAgent/useUserAgent';

type PhoneNumberLinksProps = {
  phoneNumber: string | null | undefined;
  isPrefix: boolean;
};

const PhoneNumberLinks: FC<PhoneNumberLinksProps> = ({ phoneNumber, isPrefix }) => {
  if (!phoneNumber) {
    return null;
  }
  const pn = isPrefix ? `710-${phoneNumber}` : phoneNumber;

  return <LinkButton title={pn} phoneNumber={pn} />;
};

type InformationSectionProps = {
  workplace: WfWorkplace | null;
};

const toDisplayText = (value?: string | null) => {
  if (value === null || value === undefined) return '';

  const trimmed = value.trim();
  if (trimmed === '' || trimmed === 'null') return '';

  return trimmed;
};

export function InformationSection({ workplace }: InformationSectionProps) {
  const { t } = useTranslation();
  const details = workplace?.details;

  return (
    <P_CollapsibleSection
      title={t('wf.siteDetails.information')}
      contentClassName={`grid grid-flow-row text-left ${useUserAgent().isDesktop ? '' : 'space-y-4'}`}
    >
      <FormRow title={t('wf.siteDetails.placeOfWork')}>
        <div>{toDisplayText(workplace?.name)}</div>
      </FormRow>

      <FormRow title={t('wf.siteDetails.branchOfficeName')}>
        <div>{toDisplayText(workplace?.branchName)}</div>
      </FormRow>

      <FormRow title={t('wf.siteDetails.organizationCode')}>
        <div>{toDisplayText(workplace?.organizationCode)}</div>
      </FormRow>

      <FormRow title={t('wf.common.customerNumber')}>
        <div>{toDisplayText(workplace?.customerId)}</div>
      </FormRow>

      <FormRow title={t('wf.siteDetails.customerAddressPostOffice')}>
        <div>{toDisplayText(workplace?.postalCode)}</div>
      </FormRow>

      <FormRow title={t('wf.siteDetails.customerAddress')}>
        <LinkButton
          title={workplace?.address ?? ''}
          {...(workplace?.address && { googleMapAddress: workplace.address })}
        />
      </FormRow>

      <FormRow title={`${t('wf.siteDetails.telephoneNumberDay')}: ${t('wf.common.normal')}`}>
        <PhoneNumberLinks phoneNumber={workplace?.dayPhoneNumber} isPrefix={false} />
      </FormRow>
      <FormRow title={`${t('wf.siteDetails.telephoneNumberDay')}: ${t('wf.common.prefix')}`}>
        <PhoneNumberLinks phoneNumber={workplace?.dayPhoneNumber} isPrefix={true} />
      </FormRow>
      <FormRow title={`${t('wf.siteDetails.telephoneNumberNight')}: ${t('wf.common.normal')}`}>
        <PhoneNumberLinks phoneNumber={workplace?.nightPhoneNumber} isPrefix={false} />
      </FormRow>
      <FormRow title={`${t('wf.siteDetails.telephoneNumberNight')}: ${t('wf.common.prefix')}`}>
        <PhoneNumberLinks phoneNumber={workplace?.nightPhoneNumber} isPrefix={true} />
      </FormRow>

      <FormRow title={t('wf.siteDetails.emergencyClass')}>
        <div>{toDisplayText(workplace?.emergencyClass)}</div>
      </FormRow>

      <FormRow title={t('wf.siteDetails.remarks')}>
        <div>{toDisplayText(details?.remarks)}</div>
      </FormRow>

      <FormRow title={t('wf.siteDetails.remarkSupplElevator')}>
        <div>{toDisplayText(details?.remarkSupplElevator)}</div>
      </FormRow>
      <FormRow title={t('wf.siteDetails.procedureSupplElevator')}>
        <div>{toDisplayText(details?.procedureSupplElevator)}</div>
      </FormRow>
      <FormRow title={t('wf.siteDetails.remarkSupplHeatpump')}>
        <div>{toDisplayText(details?.remarkSupplHeatpump)}</div>
      </FormRow>
      <FormRow title={t('wf.siteDetails.procedureSupplHeatpump')}>
        <div>{toDisplayText(details?.procedureSupplHeatpump)}</div>
      </FormRow>
      <FormRow title={t('wf.siteDetails.entryProcedure')}>
        <div>{toDisplayText(details?.entryProcedure)}</div>
      </FormRow>
    </P_CollapsibleSection>
  );
}
